import {hpcAttrs} from '@github-ui/hpc'
import {lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  useActiveChangeGroup,
  useCommentsPreference,
  useDiffEntries,
  useDiffEntriesDigestsWithFilter,
  useDiffSummaries,
  useDiffSummaryCount,
  useGetCollapsedDiffStatus,
  useGetProgressiveDiffEntryByPathDigest,
  useLineSpacingPreference,
  useProgressiveDiffActions,
  useSplitPreference,
  useThreadSizeInfoMap,
} from '../stores/selectors'
import {ScrollToSelectedDiff} from './ScrollToSelectedDiff'

import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {defaultRangeExtractor, type Range, useWindowVirtualizer} from '@github-ui/virtualizer'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {VirtualizedDiffRow} from './PullRequestVirtualizedDiffRow'
import virtualizedStyles from './PullRequestVirtualizedDiffsList.module.css'
import {DIFF_LIST_GAP, estimateDiffSize} from '../helpers/estimate-diff-size'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isModernPullRequestExperienceEnabled} from '@github-ui/pull-request-feature-flags'
import {BASE_DIFF_MARGIN_TOP_PADDING, MINI_HEADER_HEIGHT, STICKY_HEADER_HEIGHT} from '../helpers/constants'
import {clsx} from 'clsx'

const PullRequestDiffsAnimation = lazy(async () => {
  const module = await import('./PullRequestDiffsAnimation')
  return {default: module.PullRequestDiffsAnimation}
})

export interface PullRequestVirtualizedDiffsListProps {
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export function PullRequestVirtualizedDiffsList({
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestVirtualizedDiffsListProps) {
  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()
  const splitPreference = useSplitPreference()
  const diffsParentRef = useRef<HTMLDivElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const diffEntryDigests = useDiffEntriesDigestsWithFilter()
  const getProgressiveDiffEntry = useGetProgressiveDiffEntryByPathDigest()
  const activeChangeGroup = useActiveChangeGroup()
  const {startQueue, loadManyPriority} = useProgressiveDiffActions()

  const diffSummariesCount = useDiffSummaryCount()
  const allDiffSummaries = useDiffSummaries()
  const diffEntries = useDiffEntries()
  const threadSizeInfoMap = useThreadSizeInfoMap()

  const totalVisibleEntries = diffEntryDigests.length
  const shouldShowWhimsy = totalVisibleEntries >= 15
  const getCollapsedDiffStatus = useGetCollapsedDiffStatus()

  // Read once per list render and reuse for both the virtualizer's size estimate and each row,
  // rather than calling isFeatureEnabled per row in the hot, memoized VirtualizedDiffRow.
  const imageEstimateEnabled = isFeatureEnabled('pull_request_virtualization_image_estimate')
  const rowEstimateRefinementEnabled = isFeatureEnabled('pull_request_virtualization_loader_batching')

  useEffect(() => {
    startQueue()
  }, [startQueue])

  /**
   * Estimate size in pixels of each diff entry.
   */
  const estimateVirtualDiffSize = useCallback(
    (index: number) => {
      const digest = diffEntryDigests[index]
      if (!digest) return 0
      const progressiveDiffEntry = getProgressiveDiffEntry(digest)
      if (!progressiveDiffEntry) return 0

      const diffSummary = allDiffSummaries[progressiveDiffEntry.path ?? '']
      const isCollapsed = getCollapsedDiffStatus(progressiveDiffEntry.path ?? '')

      return estimateDiffSize({
        diffSummary,
        commentPreference,
        lineSpacingPreference,
        isCollapsed,
        threadSizeInfoMap,
        isSplitView: splitPreference === 'split',
        imageEstimateEnabled,
      })
    },
    [
      getProgressiveDiffEntry,
      diffEntryDigests,
      allDiffSummaries,
      getCollapsedDiffStatus,
      commentPreference,
      lineSpacingPreference,
      threadSizeInfoMap,
      splitPreference,
      imageEstimateEnabled,
    ],
  )

  // Key is a diffDigest
  const getItemKey = useCallback((index: number) => diffEntryDigests[index] ?? index, [diffEntryDigests])

  useEffect(() => {
    if (diffsParentRef.current) {
      setScrollMargin(diffsParentRef.current.offsetTop)
    }
  }, [])

  const defaultOverscan = 5

  const overscan = useMemo(() => {
    if (diffEntries && Object.keys(diffEntries).length === diffSummariesCount) {
      return defaultOverscan
    }
    return 0
  }, [diffEntries, diffSummariesCount])

  const pathDigestToIndex = useMemo(() => {
    const indexMap = new Map<string, number>()

    for (let index = 0; index < diffEntryDigests.length; index++) {
      const pathDigest = diffEntryDigests[index]
      if (!pathDigest) continue
      indexMap.set(pathDigest, index)
    }

    return indexMap
  }, [diffEntryDigests])

  /**
   * Custom range extractor that ensures focused and selected diffs stay in the virtual window.
   * This prevents focused elements from being unmounted during scrolling, which is critical
   * for keyboard navigation and accessibility.
   */
  const rangeExtractor = useCallback(
    (range: Range) => {
      // Find the diff that currently has focus within it
      const focusedPathDigest = document
        .querySelector('[data-path-digest]:focus-within')
        ?.getAttribute('data-path-digest')

      const focusedDiffIndex = focusedPathDigest ? (pathDigestToIndex.get(focusedPathDigest) ?? -1) : -1

      if (focusedDiffIndex < 0) {
        return defaultRangeExtractor(range)
      }

      // Extend the default range to include the focused index
      const nextRange = new Set(defaultRangeExtractor(range))
      nextRange.add(focusedDiffIndex)

      return Array.from(nextRange).sort((a, b) => a - b)
    },
    [pathDigestToIndex],
  )

  const isScrollCompensationEnabled = isFeatureEnabled('pull_request_virtualization_scroll_compensation')

  // When enabled, the virtualizer writes each row's `top` and the sizing container's `height`
  // straight to the DOM on scroll, and only re-renders React when the visible index range (or
  // `isScrolling`) changes. We use `'position'` mode so the virtualizer writes `top` — matching
  // the row's deliberate `top` positioning (avoids the Safari compositing-layer explosion that
  // `transform` would reintroduce). Rows must be registered via `measureElement` for this to
  // position them, so the row always attaches the ref when this flag is on.
  const directDomUpdatesEnabled = isFeatureEnabled('pull_request_virtualization_direct_dom_updates')

  const {
    items: virtualItems,
    totalSize,
    measureElement,
    scrollRect,
    virtualizer,
    containerRef,
  } = useWindowVirtualizer({
    count: diffEntryDigests.length,
    estimateSize: estimateVirtualDiffSize,
    getItemKey,
    overscan,
    directDomUpdates: directDomUpdatesEnabled,
    directDomUpdatesMode: 'position',
    // We use the sticky header height and base diff margin top padding
    // to ensure that we scroll to the diff header instead of a random line in a diff entry.
    scrollPaddingStart:
      BASE_DIFF_MARGIN_TOP_PADDING +
      STICKY_HEADER_HEIGHT +
      (isModernPullRequestExperienceEnabled() ? MINI_HEADER_HEIGHT : 0),
    scrollMargin,
    gap: DIFF_LIST_GAP,
    rangeExtractor,
    // Note: We intentionally do NOT use useAnimationFrameWithResizeObserver.
    // The tanstack/virtual docs recommend against it as ResizeObserver already
    // runs at an optimal time (after layout, before paint). Enabling it adds
    // ~16ms delay and provides no batching benefit.
    onChange(instance) {
      instance.shouldAdjustScrollPositionOnItemSizeChange = (item, _delta, inst) => {
        if (isScrollCompensationEnabled) {
          // Compensate scroll only when an item *above* the viewport resizes AND the user
          // is not actively scrolling backward (up).
          return item.start < (inst.scrollOffset ?? 0) && inst.scrollDirection !== 'backward'
        }
        // Don't adjust scroll position on item size changes
        // This prevents jarring viewport jumps when items resize
        return false
      }
    },
  })

  // Load data for visible items AND preload the next/previous 5 items.
  // This only fetches network data — it does NOT add items to the virtualizer's
  // rendered range, so it won't affect scroll position or layout.
  const preloadCount = 5
  // `getVirtualItems()` returns a new array on most renders, so key the effect
  // off the first/last visible indices to avoid re-running on every render.
  const firstVisibleIndex = virtualItems[0]?.index
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index
  useEffect(() => {
    if (firstVisibleIndex === undefined || lastVisibleIndex === undefined) return

    const start = Math.max(0, firstVisibleIndex - preloadCount)
    const end = Math.min(diffEntryDigests.length, lastVisibleIndex + preloadCount + 1)

    const digests = diffEntryDigests.slice(start, end).filter(digest => digest !== undefined)
    loadManyPriority(digests)
  }, [loadManyPriority, firstVisibleIndex, lastVisibleIndex, diffEntryDigests])

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (diffEntryDigests.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  const showUpdatedHeader = isModernPullRequestExperienceEnabled()

  return (
    <div
      ref={diffsParentRef}
      {...hpcAttrs()}
      data-testid="virtualized-diffs-list"
      className={clsx('d-flex flex-column gap-3', showUpdatedHeader && 'pt-2')}
    >
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      <div
        className={virtualizedStyles['virtualized-diff-row']}
        // With direct DOM updates the virtualizer owns the container's `height` (written via
        // `containerRef`), so we must NOT set it in style here. Otherwise we keep the
        // React-driven height.
        ref={directDomUpdatesEnabled ? containerRef : undefined}
        style={directDomUpdatesEnabled ? undefined : {height: `${totalSize}px`}}
      >
        {virtualItems.map(virtualRow => {
          const pathDigest = diffEntryDigests[virtualRow.index]
          if (!pathDigest) return null
          const diffEntry = getProgressiveDiffEntry(pathDigest)
          return (
            <VirtualizedDiffRow
              key={virtualRow.key}
              virtualRowIndex={virtualRow.index}
              virtualRowStart={virtualRow.start}
              virtualizedDiffEntry={diffEntry}
              diffEntry={diffEntries[diffEntry?.path ?? '']}
              diffSummary={allDiffSummaries[diffEntry?.path ?? '']}
              scrollMargin={scrollMargin}
              measureElement={measureElement}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              repository={repository}
              virtualWindowHeight={scrollRect?.height}
              imageEstimateEnabled={imageEstimateEnabled}
              rowEstimateRefinementEnabled={rowEstimateRefinementEnabled}
              directDomUpdatesEnabled={directDomUpdatesEnabled}
            />
          )
        })}
      </div>
      {shouldShowWhimsy && (
        <ErrorBoundary boundaryName="PullRequestVirtualizedDiffsListAnimation" fallback={null}>
          <Suspense fallback={null}>
            <PullRequestDiffsAnimation />
          </Suspense>
        </ErrorBoundary>
      )}

      <ScrollToSelectedDiff
        virtualizer={virtualizer}
        virtualItems={virtualItems}
        pathDigestToIndex={pathDigestToIndex}
        diffEntryDigests={diffEntryDigests}
        loadManyPriority={loadManyPriority}
      />

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}
